import { Component, OnInit, Inject } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ValidatorFn, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { LmrServices } from '../../services/lmr.service';
import { ToastrService } from 'ngx-toastr';

export interface Dealer {
  nome: string;
  nomeFantasia: string;
  razaoSocial: string;
  cnpj: string;
  inscricaoEstadual: string;
  cep: string;
  logradouro: string;
  numero: string;
  complemento: string;
  bairro: string;
  municipio: string;
  uf: string;
  telefone: string;
  email: string;
  aprovacaoAutomatica: boolean;
}

@Component({
  selector: 'app-dialog-new-dealer',
  templateUrl: './dialog-new-dealer.component.html',
  styleUrls: ['./dialog-new-dealer.component.sass'],
})
export class DialogNewDealerComponent implements OnInit {
  dealerForm!: FormGroup;
  isEdit = false;
  initialValue!: any;
  hasChanges = false;
  constructor(
    private fb: FormBuilder,
    private dialogRef: MatDialogRef<DialogNewDealerComponent>,
    @Inject(MAT_DIALOG_DATA) public dealerData: any,
    private lmrServices: LmrServices,
    private toastr: ToastrService
  ) {}

  ngOnInit(): void {
    this.initForm();

    if (this.dealerData) {
      this.isEdit = true;
      this.dealerForm.patchValue(
        {
          ...this.dealerData,
          aprovacaoAutomatica: this.dealerData.aprovacaoAutomatica ? 'sim' : 'nao',
        },
        { emitEvent: false }
      );

      this.initialValue = this.normalizeFormValue(this.dealerForm.value);
      this.dealerForm.markAsPristine();
      this.hasChanges = false;
    }

    this.dealerForm.valueChanges.subscribe((value) => {
      const current = this.normalizeFormValue(value);
      this.hasChanges = JSON.stringify(current) !== JSON.stringify(this.initialValue);
    });
  }

  initForm() {
    this.dealerForm = this.fb.group({
      nome: ['', Validators.required],
      nomeFantasia: [''],
      razaoSocial: ['', Validators.required],
      cnpj: ['', [Validators.required, this.digitsLength(14)]],
      inscricaoEstadual: ['', Validators.required],
      cep: ['', [Validators.required, this.digitsLength(8)]],
      logradouro: ['', Validators.required],
      numero: ['', Validators.required],
      complemento: [''],
      bairro: ['', Validators.required],
      municipio: ['', Validators.required],
      uf: ['', Validators.required],
      telefone: ['', [Validators.required, this.digitsLength(10, 11)]],
      email: ['', Validators.email],
      aprovacaoAutomatica: ['', Validators.required],
    });
  }

  onSubmit() {
    if (this.dealerForm.invalid) {
      this.dealerForm.markAllAsTouched();
      return;
    }

    const raw = this.dealerForm.value;
    const stripNonNumeric = (v: string) => v?.replace(/\D/g, '') ?? '';
    const payload = {
      ...raw,
      cnpj: stripNonNumeric(raw.cnpj),
      cep: stripNonNumeric(raw.cep),
      telefone: stripNonNumeric(raw.telefone),
      inscricaoEstadual: stripNonNumeric(raw.inscricaoEstadual),
      aprovacaoAutomatica: raw.aprovacaoAutomatica === 'sim',
    };
    const isEditing = Boolean(this.dealerData);

    if (isEditing) {
      this.updateDealer(this.dealerData.id, payload);
      return;
    }

    this.newDealer(payload);
  }
  normalizeFormValue(value: any) {
    const strip = (v: string) => v?.replace(/\D/g, '') ?? '';

    return {
      ...value,
      cnpj: strip(value.cnpj),
      cep: strip(value.cep),
      telefone: strip(value.telefone),
      inscricaoEstadual: strip(value.inscricaoEstadual),
    };
  }
  private handleError(error: any): void {
    const msg = error?.errors[0] ?? 'Ocorreu um erro. Tente novamente.';
    this.toastr.error(msg);
  }

  newDealer(payload: {}) {
    this.lmrServices.newDealer(payload).subscribe({
      next: () => this.dialogRef.close(true),
     error: (error) => {
        this.handleError(error);
        this.dialogRef.close(false);
      },
    });
  }

  updateDealer(idFornecedor: string, payload: {}) {
    this.lmrServices.updateDealer(idFornecedor, payload).subscribe({
      next: () => this.dialogRef.close(true),
      error: (error) => {
        this.handleError(error);
        this.dialogRef.close(false);
      },
    });
  }

  onCancel() {
    this.dialogRef.close(false);
  }

  private digitsLength(exact: number): ValidatorFn;
  private digitsLength(min: number, max: number): ValidatorFn;
  private digitsLength(min: number, max?: number): ValidatorFn {
    return (control: AbstractControl) => {
      const len = (control.value ?? '').replace(/\D/g, '').length;
      const valid = max !== undefined ? len >= min && len <= max : len === min;
      return valid ? null : { invalidLength: true };
    };
  }
}
